import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface DashboardStats {
  totalClients: number
  activeQuotes: number
  conversionRate: number
  potentialRevenue: number
  // Changements par rapport au mois dernier
  clientsChange: number
  quotesChange: number
  conversionChange: number
  revenueChange: number
}

export interface RecentContact {
  id: string
  full_name: string | null
  email: string | null
  company: string | null
  created_at: string
}

export interface RecentDevis {
  id: string
  numero: string | null
  titre: string | null
  montant_ttc: number | null
  status: string | null
  created_at: string
  client?: {
    full_name: string | null
    company: string | null
  }
}

export function useDashboardStats() {
  const [stats, setStats] = useState<DashboardStats>({
    totalClients: 0,
    activeQuotes: 0,
    conversionRate: 0,
    potentialRevenue: 0,
    clientsChange: 0,
    quotesChange: 0,
    conversionChange: 0,
    revenueChange: 0,
  })
  const [recentContacts, setRecentContacts] = useState<RecentContact[]>([])
  const [recentQuotes, setRecentQuotes] = useState<RecentDevis[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        setLoading(true)
        setError(null)

        // Date il y a 30 jours pour les comparaisons
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
        const thirtyDaysAgoISO = thirtyDaysAgo.toISOString()

        // Date il y a 60 jours pour comparer le mois précédent
        const sixtyDaysAgo = new Date()
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
        const sixtyDaysAgoISO = sixtyDaysAgo.toISOString()

        // Toutes les requêtes en parallèle
        const [
          // Clients
          totalClientsRes,
          recentClientsRes,
          previousMonthClientsRes,
          // Devis counts
          activeQuotesRes,
          previousActiveQuotesRes,
          totalDevisRes,
          acceptedDevisRes,
          previousTotalDevisRes,
          previousAcceptedDevisRes,
          // CA potentiel
          activeQuotesDataRes,
          unpaidInvoicesRes,
          previousQuotesDataRes,
          previousUnpaidInvoicesRes,
          // Récents
          contactsRes,
          devisRes,
        ] = await Promise.all([
          // 1. Total clients
          supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'client'),
          // Clients 30 derniers jours
          supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'client')
            .gte('created_at', thirtyDaysAgoISO),
          // Clients 60-30 jours
          supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'client')
            .gte('created_at', sixtyDaysAgoISO)
            .lt('created_at', thirtyDaysAgoISO),
          // 2. Devis en cours
          supabase
            .from('devis')
            .select('*', { count: 'exact', head: true })
            .in('status', ['draft', 'sent', 'viewed']),
          // Devis en cours mois dernier
          supabase
            .from('devis')
            .select('*', { count: 'exact', head: true })
            .in('status', ['draft', 'sent', 'viewed'])
            .gte('created_at', sixtyDaysAgoISO)
            .lt('created_at', thirtyDaysAgoISO),
          // 3. Total devis
          supabase
            .from('devis')
            .select('*', { count: 'exact', head: true }),
          // Devis acceptés
          supabase
            .from('devis')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'accepted'),
          // Total devis mois dernier
          supabase
            .from('devis')
            .select('*', { count: 'exact', head: true })
            .gte('created_at', sixtyDaysAgoISO)
            .lt('created_at', thirtyDaysAgoISO),
          // Devis acceptés mois dernier
          supabase
            .from('devis')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'accepted')
            .gte('created_at', sixtyDaysAgoISO)
            .lt('created_at', thirtyDaysAgoISO),
          // 4. CA potentiel - devis actifs
          supabase
            .from('devis')
            .select('montant_ttc')
            .in('status', ['sent', 'viewed']),
          // Factures impayées
          supabase
            .from('facture')
            .select('total_ttc')
            .in('statut', ['sent', 'draft']),
          // Devis actifs mois dernier
          supabase
            .from('devis')
            .select('montant_ttc')
            .in('status', ['sent', 'viewed'])
            .gte('created_at', sixtyDaysAgoISO)
            .lt('created_at', thirtyDaysAgoISO),
          // Factures impayées mois dernier
          supabase
            .from('facture')
            .select('total_ttc')
            .in('statut', ['sent', 'draft'])
            .gte('created_at', sixtyDaysAgoISO)
            .lt('created_at', thirtyDaysAgoISO),
          // 5. Contacts récents
          supabase
            .from('users')
            .select('id, full_name, email, company, created_at')
            .eq('role', 'client')
            .order('created_at', { ascending: false })
            .limit(5),
          // 6. Devis récents
          supabase
            .from('devis')
            .select('id, numero, titre, montant_ttc, status, created_at, client_id')
            .order('created_at', { ascending: false })
            .limit(5),
        ])

        if (totalClientsRes.error) throw totalClientsRes.error
        if (activeQuotesRes.error) throw activeQuotesRes.error
        if (contactsRes.error) throw contactsRes.error
        if (devisRes.error) throw devisRes.error

        // Récupérer les clients des devis en une seule requête batch
        const clientIds = (devisRes.data || [])
          .map((d: any) => d.client_id)
          .filter((id: string | null): id is string => !!id)
        const uniqueClientIds = [...new Set(clientIds)]

        let clientsMap: Record<string, { full_name: string | null; company: string | null }> = {}
        if (uniqueClientIds.length > 0) {
          const { data: clientsData } = await supabase
            .from('users')
            .select('id, full_name, company')
            .in('id', uniqueClientIds)
          if (clientsData) {
            clientsMap = Object.fromEntries(clientsData.map(c => [c.id, { full_name: c.full_name, company: c.company }]))
          }
        }

        const totalClients = totalClientsRes.count || 0
        const recentClientsCount = recentClientsRes.count || 0
        const previousMonthClients = previousMonthClientsRes.count || 0
        const activeQuotes = activeQuotesRes.count || 0
        const previousActiveQuotes = previousActiveQuotesRes.count || 0
        const totalDevis = totalDevisRes.count || 0
        const acceptedDevis = acceptedDevisRes.count || 0
        const previousTotalDevis = previousTotalDevisRes.count || 0
        const previousAcceptedDevis = previousAcceptedDevisRes.count || 0

        // Taux de conversion
        const conversionRate = totalDevis > 0
          ? (acceptedDevis / totalDevis) * 100
          : 0

        const previousConversionRate = previousTotalDevis > 0
          ? (previousAcceptedDevis / previousTotalDevis) * 100
          : 0

        // CA potentiel
        const quotesRevenue = activeQuotesDataRes.data?.reduce(
          (sum, devis) => sum + (devis.montant_ttc || 0),
          0
        ) || 0

        const invoicesRevenue = unpaidInvoicesRes.data?.reduce(
          (sum, facture) => sum + (facture.total_ttc || 0),
          0
        ) || 0

        const potentialRevenue = quotesRevenue + invoicesRevenue

        const previousQuotesRevenue = previousQuotesDataRes.data?.reduce(
          (sum, devis) => sum + (devis.montant_ttc || 0),
          0
        ) || 0

        const previousInvoicesRevenue = previousUnpaidInvoicesRes.data?.reduce(
          (sum, facture) => sum + (facture.total_ttc || 0),
          0
        ) || 0

        const previousPotentialRevenue = previousQuotesRevenue + previousInvoicesRevenue

        // Calculer les changements en pourcentage
        const clientsChange = previousMonthClients
          ? ((recentClientsCount - previousMonthClients) / previousMonthClients) * 100
          : 0

        const quotesChange = previousActiveQuotes
          ? ((activeQuotes - previousActiveQuotes) / previousActiveQuotes) * 100
          : 0

        const conversionChange = previousConversionRate > 0
          ? (conversionRate - previousConversionRate)
          : 0

        const revenueChange = previousPotentialRevenue > 0
          ? ((potentialRevenue - previousPotentialRevenue) / previousPotentialRevenue) * 100
          : 0

        // Mapper les devis avec les clients
        const devisWithClients = (devisRes.data || []).map((devis: any) => ({
          id: devis.id,
          numero: devis.numero,
          titre: devis.titre,
          montant_ttc: devis.montant_ttc,
          status: devis.status,
          created_at: devis.created_at,
          client: devis.client_id ? clientsMap[devis.client_id] : undefined,
        }))

        setStats({
          totalClients,
          activeQuotes,
          conversionRate,
          potentialRevenue,
          clientsChange,
          quotesChange,
          conversionChange,
          revenueChange,
        })

        setRecentContacts(contactsRes.data || [])
        setRecentQuotes(devisWithClients)
      } catch (err) {
        console.error('Error fetching dashboard stats:', err)
        setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  return { stats, recentContacts, recentQuotes, loading, error }
}
